import {
  Component,
  type ComponentType,
  type ContextType,
  type ErrorInfo,
  type ForwardedRef,
  type ForwardRefExoticComponent,
  forwardRef,
  type PropsWithoutRef,
  type ReactNode,
  type RefAttributes,
} from 'react'

import logger from '@/utils/logger'

import { UserContext } from '@/client/context/user'

interface Props {
  name: string
  fallback: () => ReactNode
  children: ReactNode
}

interface State {
  hasError: boolean
}

class ErrorBoundary extends Component<Props, State> {
  // eslint-disable-next-line react/static-property-placement
  declare context: ContextType<typeof UserContext>

  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(_: Error): State {
    return { hasError: true }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    const { name } = this.props
    const { id: userId } = this.context
    logger.error(`[ErrorBoundary] ${name} failed to render`, { userId, error, errorInfo })
  }

  render(): ReactNode {
    const { hasError } = this.state
    const { fallback, children } = this.props

    if (hasError) {
      return fallback()
    }

    return children
  }
}

ErrorBoundary.contextType = UserContext

export function withErrorBoundary<P, T>(
  WrappedComponent: ComponentType<P>,
  fallback: () => JSX.Element
): ForwardRefExoticComponent<PropsWithoutRef<P> & RefAttributes<T>> {
  return forwardRef((props: P, ref: ForwardedRef<T>) => (
    <ErrorBoundary name={WrappedComponent.name} fallback={fallback}>
      <WrappedComponent {...props} ref={ref} />
    </ErrorBoundary>
  ))
}
